<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Edit Withdrawal Log</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.user_id"
                  :items="users"
                  item-text="text"
                  item-value="value"
                  outlined
                  label="User *"
                  prepend-inner-icon="mdi-account"
                  :error-messages="errors.user_id"
                  :loading="loadingUsers"
                  @change="loadBankDetails"
                  :disabled="!canEdit()"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.bank_detail_id"
                  :items="bankDetails"
                  item-text="text"
                  item-value="value"
                  outlined
                  label="Bank Details"
                  prepend-inner-icon="mdi-bank"
                  :error-messages="errors.bank_detail_id"
                  :loading="loadingBankDetails"
                  :disabled="!form.user_id || !canEdit()"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.transfer_id"
                  outlined
                  label="Transfer ID *"
                  prepend-inner-icon="mdi-identifier"
                  :error-messages="errors.transfer_id"
                  :disabled="!canEdit()"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.transfer_amount"
                  outlined
                  label="Transfer Amount (in Paise) *"
                  prepend-inner-icon="mdi-currency-inr"
                  :error-messages="errors.transfer_amount"
                  type="number"
                  hint="Enter amount in paise (e.g., 150000 for ₹1500)"
                  persistent-hint
                  :disabled="!canEdit()"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.transfer_mode"
                  :items="transferModeOptions"
                  outlined
                  label="Transfer Mode"
                  prepend-inner-icon="mdi-bank-transfer"
                  :error-messages="errors.transfer_mode"
                  :disabled="!canEdit()"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.fundsource_id"
                  outlined
                  label="Fund Source ID"
                  prepend-inner-icon="mdi-source-branch"
                  :error-messages="errors.fundsource_id"
                  :disabled="!canEdit()"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.status"
                  :items="statusOptions"
                  outlined
                  label="Status"
                  prepend-inner-icon="mdi-toggle-switch"
                  :error-messages="errors.status"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.status_code"
                  outlined
                  label="Status Code"
                  prepend-inner-icon="mdi-numeric"
                  :error-messages="errors.status_code"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-switch
                  v-model="form.is_active"
                  label="Active"
                  :disabled="!canEdit()"
                ></v-switch>
              </v-col>
              <v-col sm="12">
                <v-textarea
                  v-model="form.status_description"
                  outlined
                  label="Status Description"
                  prepend-inner-icon="mdi-text"
                  :error-messages="errors.status_description"
                  rows="3"
                ></v-textarea>
              </v-col>
              <v-col sm="12">
                <v-textarea
                  v-model="form.remarks"
                  outlined
                  label="Remarks"
                  prepend-inner-icon="mdi-comment-text"
                  :error-messages="errors.remarks"
                  rows="3"
                  :disabled="!canEdit()"
                ></v-textarea>
              </v-col>
            </v-row>
            
            <!-- Read-only Information -->
            <v-row v-if="withdrawalLog">
              <v-col sm="6">
                <v-text-field
                  :value="withdrawalLog.created_by ? `${withdrawalLog.created_by.first_name} ${withdrawalLog.created_by.last_name}` : 'N/A'"
                  outlined
                  label="Created By"
                  prepend-inner-icon="mdi-account-plus"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  :value="formatDate(withdrawalLog.created_at)"
                  outlined
                  label="Created At"
                  prepend-inner-icon="mdi-calendar-plus"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  :value="formatDate(withdrawalLog.updated_at)"
                  outlined
                  label="Updated At"
                  prepend-inner-icon="mdi-calendar-edit"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col sm="12" v-if="withdrawalLog.formatted_transfer_amount">
                <v-alert type="info" text>
                  <strong>Formatted Amount:</strong> ₹{{ withdrawalLog.formatted_transfer_amount }}
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            @click="softDelete"
            v-if="!withdrawalLog?.is_deleted && canDelete()"
            :loading="deletingLoader"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
          <v-btn
            color="success"
            @click="restore"
            v-if="withdrawalLog?.is_deleted && canDelete()"
            :loading="restoringLoader"
          >
            <v-icon left>mdi-restore</v-icon>
            Restore
          </v-btn>
          <v-btn
            color="primary"
            @click="update"
            :loading="loading"
            :disabled="!valid || loading || !canEdit()"
            v-if="!withdrawalLog?.is_deleted"
          >
            <v-icon left>mdi-content-save</v-icon>
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      valid: false,
      loading: false,
      deletingLoader: false,
      restoringLoader: false,
      loadingUsers: false,
      loadingBankDetails: false,
      withdrawalLog: null,
      users: [],
      bankDetails: [],
      form: {
        user_id: null,
        bank_detail_id: null,
        transfer_id: null,
        transfer_amount: null,
        transfer_mode: null,
        fundsource_id: null,
        status: null,
        status_code: null,
        status_description: null,
        remarks: null,
        is_active: true,
      },
      transferModeOptions: [
        { text: "Bank Transfer", value: "banktransfer" },
        { text: "RTGS", value: "rtgs" },
        { text: "NEFT", value: "neft" },
        { text: "IMPS", value: "imps" },
        { text: "UPI", value: "upi" },
      ],
      statusOptions: [
        { text: "Pending", value: "PENDING" },
        { text: "Success", value: "SUCCESS" },
        { text: "Failed", value: "FAILED" },
        { text: "Cancelled", value: "CANCELLED" },
        { text: "Processing", value: "PROCESSING" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      errors: "errors/errors",
    }),
  },
  mounted() {
    this.loadUsers();
    this.getData();
  },
  methods: {
    async getData() {
      try {
        const response = await axios.get(`/withdrawal_logs/${this.$route.params.id}`);
        if (response.data.success) {
          this.withdrawalLog = response.data.data;
          this.populateForm();
          if (this.withdrawalLog.user_id) {
            await this.loadBankDetails();
          }
        }
      } catch (error) {
        console.error("Error fetching withdrawal log:", error);
        this.$store.dispatch("errors/setError", error);
      }
    },

    populateForm() {
      this.form = {
        user_id: this.withdrawalLog.user_id,
        bank_detail_id: this.withdrawalLog.bank_detail_id,
        transfer_id: this.withdrawalLog.transfer_id,
        transfer_amount: this.withdrawalLog.transfer_amount,
        transfer_mode: this.withdrawalLog.transfer_mode,
        fundsource_id: this.withdrawalLog.fundsource_id,
        status: this.withdrawalLog.status,
        status_code: this.withdrawalLog.status_code,
        status_description: this.withdrawalLog.status_description,
        remarks: this.withdrawalLog.remarks,
        is_active: this.withdrawalLog.is_active,
      };
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/users");
        this.users = response.data.data.map((user) => ({
          text: `${user.first_name} ${user.last_name} (${user.phone})`,
          value: user.id,
        }));
      } catch (error) {
        console.error("Error loading users:", error);
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadBankDetails() {
      if (!this.form.user_id) {
        this.bankDetails = [];
        this.form.bank_detail_id = null;
        return;
      }

      this.loadingBankDetails = true;
      try {
        const response = await axios.get("/bank_details", {
          params: { user_id: this.form.user_id },
        });
        this.bankDetails = response.data.data.map((bank) => ({
          text: `${bank.name} - ${bank.account_number} (${bank.bank_name})`,
          value: bank.id,
        }));
      } catch (error) {
        console.error("Error loading bank details:", error);
        this.bankDetails = [];
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.loadingBankDetails = false;
      }
    },

    async update() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      try {
        const response = await axios.patch(
          `/withdrawal_logs/${this.$route.params.id}`,
          this.form
        );
        
        if (response.data.success) {
          this.$toast.success(response.data.sub_title || "Withdrawal log updated successfully");
          this.getData(); // Refresh data
        } else {
          this.$toast.error(response.data.sub_title || "Error updating withdrawal log");
        }
      } catch (error) {
        console.error("Error updating withdrawal log:", error);
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.loading = false;
      }
    },

    async softDelete() {
      if (!confirm("Are you sure you want to delete this withdrawal log?")) {
        return;
      }

      this.deletingLoader = true;
      try {
        const response = await axios.post(
          `/withdrawal_logs/delete/${this.$route.params.id}`
        );
        
        if (response.data.success) {
          this.$toast.success(response.data.sub_title || "Withdrawal log deleted successfully");
          this.$router.push("/withdrawal_logs");
        } else {
          this.$toast.error(response.data.sub_title || "Error deleting withdrawal log");
        }
      } catch (error) {
        console.error("Error deleting withdrawal log:", error);
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.deletingLoader = false;
      }
    },

    async restore() {
      if (!confirm("Are you sure you want to restore this withdrawal log?")) {
        return;
      }

      this.restoringLoader = true;
      try {
        const response = await axios.post(
          `/withdrawal_logs/restore/${this.$route.params.id}`
        );
        
        if (response.data.success) {
          this.$toast.success(response.data.sub_title || "Withdrawal log restored successfully");
          this.getData(); // Refresh data
        } else {
          this.$toast.error(response.data.sub_title || "Error restoring withdrawal log");
        }
      } catch (error) {
        console.error("Error restoring withdrawal log:", error);
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.restoringLoader = false;
      }
    },

    canEdit() {
      return ["admin", "super_admin"].includes(this.user?.role);
    },

    canDelete() {
      return ["admin", "super_admin"].includes(this.user?.role);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>